import React, { useState, useEffect } from 'react';
import { Calendar, User, Clock, ArrowRight, Search, Filter, TrendingUp, BookOpen, Coins, PieChart, BarChart3, Shield, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { blogService } from '../services/blogService';
import { newsletterService } from '../services/newsletterService';
import './Blog.css';
import { useNavigate } from 'react-router-dom';

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null); // 'success', 'error', 'already_subscribed'
  const [isSubscribing, setIsSubscribing] = useState(false);
  const navigate = useNavigate();

  // Load blog posts from database
  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      const posts = await blogService.getPosts();
      setBlogPosts(posts);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      setIsSubscribing(true);
      setNewsletterStatus(null);

      // Check if already subscribed
      const isAlreadySubscribed = await newsletterService.checkSubscription(email);
      
      if (isAlreadySubscribed) {
        setNewsletterStatus('already_subscribed');
        return;
      }

      // Subscribe to newsletter
      await newsletterService.subscribe(email);
      setNewsletterStatus('success');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterStatus('error');
    } finally {
      setIsSubscribing(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Trading Insights': <TrendingUp />,
      'Educational': <BookOpen />,
      'Cryptocurrency': <Coins />,
      'Portfolio Management': <PieChart />,
      'Technical Analysis': <BarChart3 />,
      'Risk Management': <Shield />
    };
    return icons[category] || <FileText />;
  };

  const categories = ['all', 'Trading Insights', 'Educational', 'Cryptocurrency', 'Portfolio Management', 'Technical Analysis', 'Risk Management'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="blog">
        <section className="blog-hero">
          <div className="container">
            <h1>Kenny Miles Exchange Blog</h1>
            <p>Loading blog posts...</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="blog">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <h1>Kenny Miles Exchange Blog</h1>
          <p>
            Insights, strategies, and analysis from trading experts to help you 
            make informed decisions in the financial markets.
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="section">
        <div className="container">
          <div className="search-filter-container">
            <div className="search-box">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-container">
              <Filter className="filter-icon" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section blog-posts-section">
        <div className="container">
          {filteredPosts.length > 0 ? (
            <div className="blog-grid">
              {filteredPosts.map(post => (
                <article key={post.id} className="blog-card">
                  <div className="blog-image">
                    <div className="category-icon">
                      {getCategoryIcon(post.category)}
                    </div>
                  </div>
                  
                  <div className="blog-content">
                    <div className="blog-meta">
                      <span className="blog-category">{post.category}</span>
                      <div className="blog-details">
                        <span className="blog-author">
                          <User className="meta-icon" />
                          Kenny Miles
                        </span>
                        <span className="blog-date">
                          <Calendar className="meta-icon" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                        <span className="blog-read-time">
                          <Clock className="meta-icon" />
                          {Math.ceil(post.content.length / 500)} min read
                        </span>
                      </div>
                    </div>
                    
                    <h2 className="blog-title">{post.title}</h2>
                    <p className="blog-excerpt">{post.excerpt}</p>
                    
                    <button 
                      className="read-more-btn"
                      onClick={() => navigate(`/blog/${post.id}`)}
                    >
                      Read More
                      <ArrowRight className="arrow-icon" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <h3>No articles found</h3>
              <p>Try adjusting your search terms or category filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section bg-light">
        <div className="container">
          <div className="newsletter-content text-center">
            <h2>Stay Updated with Our Latest Insights</h2>
            <p className="mb-6">
              Subscribe to our newsletter and get the latest trading insights, 
              market analysis, and exclusive content delivered to your inbox.
            </p>
            
            {/* Replace this div with your Mailchimp form code */}
            <div className="mailchimp-form">
              <div id="mc_embed_shell">
                <div id="mc_embed_signup">
                  <form action="https://gmail.us12.list-manage.com/subscribe/post?u=7e40b32ef8972fdbd39a191db&amp;id=44a97da539&amp;f_id=00d60ee9f0" method="post" id="mc-embedded-subscribe-form" name="mc-embedded-subscribe-form" className="validate" target="_self" noValidate>
                    <div id="mc_embed_signup_scroll">
                      <h2>Subscribe</h2>
                      <div className="indicates-required">
                        <span className="asterisk">*</span> indicates required
                      </div>
                      <div className="mc-field-group">
                        <label htmlFor="mce-EMAIL">Email Address <span className="asterisk">*</span></label>
                        <input type="email" name="EMAIL" className="required email" id="mce-EMAIL" required value="" />
                      </div>
                      <div id="mce-responses" className="clear foot">
                        <div className="response" id="mce-error-response" style={{display: 'none'}}></div>
                        <div className="response" id="mce-success-response" style={{display: 'none'}}></div>
                      </div>
                      <div aria-hidden="true" style={{position: 'absolute', left: '-5000px'}}>
                        <input type="text" name="b_7e40b32ef8972fdbd39a191db_44a97da539" tabIndex="-1" value="" />
                      </div>
                      <div className="optionalParent">
                        <div className="clear foot">
                          <input type="submit" name="subscribe" id="mc-embedded-subscribe" className="button newsletter-btn" value="Subscribe" />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog; 